import { useRef, useState } from 'react'

import { clampCropPosition } from '../../../UploadImage.utils'

import { DRAG_MODE } from './useCropDrag.constants'
import {
  getDragCursor,
  getStagePoint,
  isDragModeResize,
  resizeCrop,
} from './useCropDrag.utils'

// Pointer move/resize of the crop frame; `onCropChange` receives the next crop
// for every user-driven change. In-flight drag state lives in a ref.
export function useCropDrag({ aspect, crop, layout, onCropChange }) {
  const dragStateRef = useRef(null)

  const [activeDrag, setActiveDrag] = useState(null)

  function handlePointerDown(event) {
    const { corner, role } = event.target.dataset

    if (layout && crop && (corner || role === 'frame')) {
      const mode = corner ? DRAG_MODE.RESIZE : DRAG_MODE.MOVE

      dragStateRef.current = {
        corner: corner || null,
        mode,
        startCrop: crop,
        startPoint: getStagePoint(event),
      }
      setActiveDrag({ corner: corner || null, mode })
      event.currentTarget.setPointerCapture(event.pointerId)
    }
  }

  function handlePointerMove(event) {
    const dragState = dragStateRef.current

    if (dragState) {
      const point = getStagePoint(event)

      if (isDragModeResize(dragState.mode)) {
        onCropChange(
          resizeCrop({
            aspect,
            corner: dragState.corner,
            layout,
            pointerX: point.x,
            pointerY: point.y,
            startCrop: dragState.startCrop,
          })
        )
      } else {
        const deltaX = point.x - dragState.startPoint.x
        const deltaY = point.y - dragState.startPoint.y

        if (deltaX !== 0 || deltaY !== 0) {
          onCropChange(
            clampCropPosition({
              crop: {
                ...dragState.startCrop,
                x: dragState.startCrop.x + deltaX,
                y: dragState.startCrop.y + deltaY,
              },
              layout,
            })
          )
        }
      }
    }
  }

  function handlePointerUp(event) {
    if (dragStateRef.current) {
      dragStateRef.current = null
      setActiveDrag(null)
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
  }

  return {
    dragCursor: getDragCursor(activeDrag),
    isDragging: Boolean(activeDrag),
    onPointerDown: handlePointerDown,
    onPointerMove: handlePointerMove,
    onPointerUp: handlePointerUp,
  }
}
